import { useId, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_STROKE_WIDTH_STEPS,
  MIN_STROKE_WIDTH_STEPS,
  type ITextStyle,
  type MarkupColor,
  type MarkupTool,
} from '../../../utils/markupTypes';
import { ToolButtons } from './ToolButtons';
import { ColorSwatches } from './ColorSwatches';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { TextFormatControls } from './TextFormatControls';
import { HistoryControls } from './HistoryControls';
import styles from './Toolbar.module.css';

const MIN_WIDTH_THUMB_PX = 6;
const MAX_WIDTH_THUMB_PX = 16;

interface IToolbarProps {
  tool: MarkupTool;
  color: MarkupColor;
  canUndo: boolean;
  canRedo: boolean;
  canClearPage: boolean;
  disabled: boolean;
  textStyle: ITextStyle;
  strokeWidthSteps: number; // The active tool family's line width on the 1–10 scale
  textSelected: boolean; // A text stamp is selected — its format controls show in any tool
  onToolChange: (tool: MarkupTool) => void;
  onColorChange: (color: MarkupColor) => void;
  onStrokeWidthChange: (steps: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onClearPage: () => void;
  onTextStyleChange: (patch: Partial<ITextStyle>) => void;
}

/**
 * The drawing tools, on one line: tools, colours, the contextual width slider
 * and text formatting, then history. Each block is its own component; this file
 * decides which blocks show when.
 */
export function Toolbar({
  tool, color, canUndo, canRedo, canClearPage, disabled, textStyle, strokeWidthSteps, textSelected,
  onToolChange, onColorChange, onStrokeWidthChange, onUndo, onRedo, onClearPage,
  onTextStyleChange,
}: IToolbarProps) {
  const { t } = useTranslation();
  const widthInputId = useId();
  const toolHasWidth = tool !== 'select' && tool !== 'text' && tool !== 'eraser';
  const widthPercent = (strokeWidthSteps - MIN_STROKE_WIDTH_STEPS) / (MAX_STROKE_WIDTH_STEPS - MIN_STROKE_WIDTH_STEPS);
  const widthThumbPx = MIN_WIDTH_THUMB_PX + widthPercent * (MAX_WIDTH_THUMB_PX - MIN_WIDTH_THUMB_PX);

  return (
    <div className={styles.toolbar} role="toolbar" aria-label={t('documents.markup.toolbar')}>
      <ToolButtons tool={tool} disabled={disabled} onToolChange={onToolChange} />

      <ColorSwatches color={color} disabled={disabled} onColorChange={onColorChange} />

      {/** Line width — only for tools that draw a line. */}
      {toolHasWidth && (
        <div className={styles.toolGroup}>
          <div className={styles.widthSliderWrap}>
            <label className={styles.widthLabel} htmlFor={widthInputId}>{t('documents.markup.width')}</label>
            <Tooltip content={`${t('documents.markup.width')}: ${strokeWidthSteps}`}>
              <input
                id={widthInputId}
                type="range"
                className={styles.widthSlider}
                value={strokeWidthSteps}
                min={MIN_STROKE_WIDTH_STEPS}
                max={MAX_STROKE_WIDTH_STEPS}
                step={1}
                onChange={(e) => onStrokeWidthChange(Number(e.target.value))}
                disabled={disabled}
                style={{ '--width-thumb-size': `${widthThumbPx}px` } as CSSProperties}
              />
            </Tooltip>
          </div>
        </div>
      )}

      {/** Text options — in text mode */}
      {(tool === 'text' || textSelected) && (
        <TextFormatControls textStyle={textStyle} disabled={disabled} onTextStyleChange={onTextStyleChange} />
      )}

      <HistoryControls
        canUndo={canUndo}
        canRedo={canRedo}
        canClearPage={canClearPage}
        disabled={disabled}
        onUndo={onUndo}
        onRedo={onRedo}
        onClearPage={onClearPage}
      />
    </div>
  );
}